import React, { useCallback, useEffect, useRef, useState } from 'react';
import useOnClickOutside from 'hooks/useOnClickOutside';
import getSearchURL from 'url-constructors/getSearchURL';
import isChildOf from 'helpers/isChildOf';
import { useSelector, useDispatch } from 'react-redux';

import SearchInput from './SearchInput';
import RadioGroup from 'components/base/RadioGroup/RadioGroup';
import Dropdown from 'components/base/Dropdown/Dropdown';
import ResultsSection from 'components/search/ResultsSection/ResultsSection';

import AddEntityToListView from 'components/customlists/AddToList/AddEntityToListView';
import { logToAmplitude } from 'helpers/logAmplitudeEvent';

import { EntityTypes } from 'context/entityContext';
import { IEntityInfo } from 'pages/Search';

import debounce from 'lodash.debounce';
import RecentEntities from '../RecentEntities/RecentEntities';
import {
  ButtonWrapper,
  ResultDropdownSection,
  ResultItemsWrapper,
  SearchWrapper,
  RecentEntitiesWrapper,
  AddToListSection,
  CompilationSwitcher,
} from './SearchDropdown.styles';
import useMedia from 'hooks/useMedia';
import {
  loadRecentEntities,
  resetEnitityUsage,
} from 'redux/reducers/usage/entityUsage';
import { IRecentEntityRaw } from 'helpers/interceptors/recentListMetadata';
import { PromiseState } from 'react-refetch';
import interceptConnect from 'helpers/interceptConnect';
import { IEntity } from '../ResultsSection/ResultsList/ResultsList';
import { trackEntity } from 'redux/reducers/usage/entityUsage';

import { apiTypeToEntityType } from 'helpers/mapEntityTypes';
import RecentEntitiesSection from './RecentEntitiesSection';
import supportsOneOf from 'helpers/supportsOneOf';
import Switcher from 'components/Switcher';

interface ISearchDropdownProps {
  isExternalUser: boolean;
  recentMetadataFetchData: PromiseState<any>;
  fetchRecentEntitiesMetadata: (
    recentList: IRecentEntityRaw[] | IRecentItem[]
  ) => any;
}

export interface IRecentItem {
  entityId: string;
  entityType: string;
  lastVisit: string;
  visitAmount: number;
}

const dataSources: any = () => ({
  fetchRecentEntitiesMetadata: (
    recentList: IRecentEntityRaw[]
  ): {
    recentMetadataFetchData: {
      url: string;
      interceptionData: {
        recentList: IRecentEntityRaw[];
      };
      comparison: IRecentEntityRaw[];
    };
  } => {
    return {
      recentMetadataFetchData: {
        url: `recentlistmetadata`,
        interceptionData: { recentList },
        comparison: recentList,
      },
    };
  },
});

const SearchDropdown: React.FC<ISearchDropdownProps> = (props) => {
  const {
    isExternalUser,
    fetchRecentEntitiesMetadata,
    recentMetadataFetchData = PromiseState.create(),
  } = props;
  const dispatch = useDispatch();
  const entityTypes = [
    { label: 'All results', value: 'all' },
    { label: 'Tracks', value: 'track' },
    { label: 'Releases', value: 'release' },
    { label: 'Artists', value: 'artist' },
    { label: 'Projects', value: 'project' },
  ];
  if (!isExternalUser) {
    entityTypes.push({ label: 'Labels', value: 'label' });
  }

  const { recentData } =
    !recentMetadataFetchData.pending && recentMetadataFetchData?.value;

  const isSmallScreen = useMedia('(max-width: 40em)');

  const [inputValue, setInputValue] = useState('');
  const [searchValue, setSearchValue] = useState<string>('');
  const [isVisibleDropdown, setIsVisibleDropdown] = useState(false);
  const [entityType, setEntityType] = useState('all');
  const [entityInfo, setEntityInfo] = useState<IEntityInfo | undefined>();
  const [isAddToListView, setIsAddToListView] = useState(false);
  const [startSearchTime, setStartSearchTime] = useState<Date | undefined>();
  const [isAllAlbumSearch, setIsAllAlbumSearch] = useState<boolean>(true);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const updateSearchValue = (value: string): void => setSearchValue(value);

  useEffect(() => {
    dispatch(loadRecentEntities());
  }, [dispatch]);

  const recentList: IRecentItem[] = useSelector(
    (state: { entityUsage: { recentEntities: [] } }) =>
      state.entityUsage.recentEntities
  );

  const isLoadingRecent =
    recentMetadataFetchData.pending && recentList && recentList.length > 0;

  useEffect(() => {
    if (recentList && recentList.length > 0) {
      fetchRecentEntitiesMetadata(recentList);
    }
  }, [recentList, fetchRecentEntitiesMetadata]);

  useEffect(() => {
    setStartSearchTime(new Date());
  }, [searchValue]);

  const debouncedUpdateSearchValue = useCallback(
    debounce(updateSearchValue, 500),
    []
  );

  useOnClickOutside(dropdownRef, (el: any) => {
    if (
      isVisibleDropdown &&
      !isChildOf(el.target, 'menuList') &&
      !isChildOf(el.target, 'addtolist')
    ) {
      setIsVisibleDropdown(false);
      setInputValue('');
      setEntityType('all');
      setSearchValue('');
      setEntityInfo(undefined);
      setIsAddToListView(false);
      setIsAllAlbumSearch(true);
    }
  });

  /** Callback for submitting the search field */
  const handleSubmit = (event: any): void => {
    event.preventDefault();
    (document.activeElement as any)?.blur();
  };

  const handleChange = ({ target: { value } }: { target: any }): void => {
    setInputValue(value);
    debouncedUpdateSearchValue(value);
  };

  const handleEntityChange = (
    value: string,
    event?: React.MouseEvent
  ): void => {
    if (event) event.stopPropagation();
    setEntityType(value);
  };

  const handleOnFocus = (): void => {
    if (!isVisibleDropdown) setIsVisibleDropdown(true);
  };

  const getSearchTime = (startTime: Date): number => {
    const endSearchTime = new Date().valueOf();
    return (endSearchTime - startTime.valueOf()) / 1000;
  };

  const onAddToListClick = (entityInfo: IEntityInfo): void => {
    setIsAddToListView(true);
    setEntityInfo(entityInfo);
    const searchTime = getSearchTime(startSearchTime || new Date());
    logToAmplitude('Search', { searchValue, searchTime });
  };

  const hideModal = (): void => {
    setEntityInfo(undefined);
    setIsAddToListView(false);
  };

  const addToRecentItems = (entityInfo: IEntity): void => {
    dispatch(trackEntity(entityInfo.id, apiTypeToEntityType(entityInfo.type)));
  };

  const onOpenEntity = (entityInfo: IEntity): void => {
    const searchTime = getSearchTime(startSearchTime || new Date());
    logToAmplitude('Search', { searchValue, searchTime });
    addToRecentItems(entityInfo);
  };

  const clearRecentItems = useCallback(
    () => dispatch(resetEnitityUsage()),
    [dispatch]
  );

  const getSpeechSearchValue = (searchStr: string): void => {
    setIsVisibleDropdown(true);
    setSearchValue(searchStr);
    setInputValue(searchStr);
  };

  return (
    <>
      <SearchWrapper ref={dropdownRef} isIE={!supportsOneOf()}>
        {!isAddToListView && (
          <>
            <SearchInput
              searchValue={inputValue}
              active={isVisibleDropdown}
              handleSubmit={handleSubmit}
              handleChange={handleChange}
              handleOnFocus={handleOnFocus}
              getSpeechSearchValue={getSpeechSearchValue}
            />
            {isVisibleDropdown && (
              <ResultDropdownSection>
                {!isSmallScreen ? (
                  <RadioGroup
                    options={entityTypes}
                    name="entity"
                    selectedOption={entityType}
                    onChange={({ value }: { value: string }): void =>
                      setEntityType(value)
                    }
                    /** The parameter to identify the buttons with new design
                     * green active button, white with green border button and border radiuses
                     */
                    isButtonNewDesigned
                    isSearchTabs
                  />
                ) : (
                  <Dropdown
                    items={entityTypes}
                    selectedItem={entityTypes.find(
                      (obj) => obj.value === entityType
                    )}
                    onChange={({ value }: { value: string }): void =>
                      handleEntityChange(value)
                    }
                    isToggleCaret={true}
                    minHeight={isSmallScreen ? '60px' : undefined}
                    /** The parameter to identify the dropdowns with new design (border radiuses) */
                    isRoundedDropdown="all"
                  />
                )}

                <ResultItemsWrapper>
                  <>
                    {entityType === 'release' && (
                      <CompilationSwitcher alignSelf="center">
                        <Switcher
                          componentName="search-releases"
                          isChecked={isAllAlbumSearch}
                          rightLabel="View Compilations"
                          setCheck={setIsAllAlbumSearch}
                          isToggleSwitcher
                        />
                      </CompilationSwitcher>
                    )}
                    {searchValue.length === 0 ? (
                      entityType === 'all' ? (
                        <RecentEntitiesSection
                          isLoadingRecent={isLoadingRecent}
                          onAddToListClick={onAddToListClick}
                          addToRecentItems={onOpenEntity}
                          clearRecentItems={clearRecentItems}
                          recentMetadata={recentData}
                          recentList={recentList}
                          searchValue={searchValue}
                          activeEntityType={entityType}
                          isSmallScreen={isSmallScreen}
                        />
                      ) : (
                        <RecentEntitiesWrapper>
                          <RecentEntities
                            data={[]}
                            loading={false}
                            onAddToListClick={onAddToListClick}
                          />
                        </RecentEntitiesWrapper>
                      )
                    ) : entityType === 'all' ? (
                      <>
                        <RecentEntitiesSection
                          isLoadingRecent={isLoadingRecent}
                          onAddToListClick={onAddToListClick}
                          addToRecentItems={onOpenEntity}
                          clearRecentItems={clearRecentItems}
                          recentMetadata={recentData}
                          recentList={recentList}
                          searchValue={searchValue}
                          activeEntityType={entityType}
                          isSmallScreen={isSmallScreen}
                        />
                        {entityTypes.map(({ label, value }) =>
                          value === 'all' ? null : (
                            <ResultsSection
                              key={value}
                              entityType={value}
                              title={label}
                              url={getSearchURL({
                                entityType: value as EntityTypes,
                                limit: 6,
                                query: searchValue,
                              })}
                              searchValue={searchValue}
                              displayLimit={5}
                              onAddToListClick={onAddToListClick}
                              onOpenEntity={onOpenEntity}
                            >
                              <ButtonWrapper>
                                <button
                                  onClick={(event: React.MouseEvent): void =>
                                    handleEntityChange(value, event)
                                  }
                                >
                                  Show all {value} results
                                </button>
                              </ButtonWrapper>
                            </ResultsSection>
                          )
                        )}
                      </>
                    ) : (
                      <>
                        <ResultsSection
                          title={`${entityType}s`}
                          url={getSearchURL({
                            entityType: entityType as EntityTypes,
                            query: searchValue,
                            releaseData:
                              entityType === 'release' && !isAllAlbumSearch
                                ? 'compilation'
                                : undefined,
                          })}
                          entityType={
                            entityType === 'release' && !isAllAlbumSearch
                              ? 'compilation'
                              : entityType
                          }
                          inputValue={inputValue}
                          searchValue={searchValue}
                          onAddToListClick={onAddToListClick}
                          onOpenEntity={onOpenEntity}
                        />
                      </>
                    )}
                  </>
                </ResultItemsWrapper>
              </ResultDropdownSection>
            )}
          </>
        )}
        {isAddToListView && entityInfo && (
          <AddToListSection>
            <AddEntityToListView
              close={hideModal}
              entityId={entityInfo.entityId}
              entityName={entityInfo.entityName}
              entityType={entityInfo.apiEntityType}
            />
          </AddToListSection>
        )}
      </SearchWrapper>
    </>
  );
};

export default interceptConnect<ISearchDropdownProps>(dataSources)(
  SearchDropdown
) as any;
